import { useState, useEffect } from 'react';
import { Check, XCircle, Play, Square, Loader2, Car } from 'lucide-react';
import { usePerInstallationWork } from '../hooks/usePerInstallationWork';
import { TechnicianNotesEditor } from './WorkTrackingPanel';

// Formatta durata elapsed in HH:MM:SS
const formatElapsed = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Timer live per una singola installation in_progress
const InstallationTimer = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [startedAt]);
  return (
    <span className="font-mono font-bold text-green-700 text-lg" data-testid="installation-timer">
      {formatElapsed(elapsed)}
    </span>
  );
};

const statusBadge = (status) => {
  const map = {
    pending:       { label: 'Da iniziare', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    in_progress:   { label: 'In corso',    cls: 'bg-green-100 text-green-800 border-green-300' },
    completed:     { label: 'Completato',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    not_completed: { label: 'Non completato', cls: 'bg-red-100 text-red-800 border-red-300' },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
  );
};

const vehicleTitle = (inst, idx) => {
  const parts = [
    inst.vehicle_brand,
    inst.vehicle_model,
  ].filter(Boolean).join(' ');
  const plate = inst.license_plate ? ` (${inst.license_plate})` : '';
  const job = inst.job_number ? ` · Job ${inst.job_number}` : '';
  return `Veicolo ${idx + 1}${parts ? ' - ' + parts : ''}${plate}${job}`;
};

const PerInstallationWorkTrackingPanel = ({ appointment, canStartStopWork, onRefresh, t, currentUser }) => {
  const work = usePerInstallationWork(appointment, onRefresh, t);

  if (!canStartStopWork) return null;
  const installations = appointment.installations || [];
  if (installations.length <= 1) return null;

  const isAssignedInstaller = currentUser?.role === 'installer' && appointment.assigned_installer_id === currentUser?.id;

  return (
    <div className="border-t pt-4 mt-4" data-testid="per-installation-work-tracking">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800">Chiusura per veicolo</h3>
        <span className="text-xs text-gray-500">
          {installations.length} veicoli - chiudi ogni veicolo separatamente
        </span>
      </div>

      {isAssignedInstaller && (
        <TechnicianNotesEditor
          appointmentId={appointment.id}
          initialValue={appointment.technician_notes || ''}
          onSaved={onRefresh}
        />
      )}

      <div className="space-y-3">
        {installations.map((inst, idx) => {
          const status = (inst.status || 'pending').toLowerCase();
          const isPending = status === 'pending';
          const isInProgress = status === 'in_progress';
          const isCompleted = status === 'completed';
          const isNotCompleted = status === 'not_completed';
          const busy = work.busyInstallationId === inst.id;
          const showingEndForm = work.expandedEndFormId === inst.id;
          const form = work.endForms[inst.id] || { status: 'completed', reason: '', category: '' };

          const cardTone =
            isInProgress ? 'bg-green-50 border-green-200'
            : isCompleted ? 'bg-emerald-50 border-emerald-200'
            : isNotCompleted ? 'bg-red-50 border-red-200'
            : 'bg-white border-gray-200';

          return (
            <div
              key={inst.id || idx}
              className={`rounded-lg border p-3 ${cardTone}`}
              data-testid={`installation-card-${inst.id || idx}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <Car size={18} className="text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {vehicleTitle(inst, idx)}
                  </p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {statusBadge(status)}
                    {isInProgress && inst.work_started_at && <InstallationTimer startedAt={inst.work_started_at} />}
                    {(isCompleted || isNotCompleted) && inst.actual_duration != null && (
                      <span className="text-xs text-gray-600">
                        Durata: {Math.floor(inst.actual_duration / 60)}h {inst.actual_duration % 60}min
                      </span>
                    )}
                  </div>
                  {(isCompleted || isNotCompleted) && inst.status_reason && (
                    <p className="mt-1 text-xs text-gray-600 italic">
                      {inst.status_reason}
                    </p>
                  )}
                </div>
              </div>

              {isPending && (
                <button
                  onClick={() => work.startInstallation(inst.id)}
                  disabled={busy || work.gettingLocation}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm disabled:opacity-50"
                  data-testid={`start-installation-btn-${inst.id || idx}`}
                >
                  {busy ? (
                    <><Loader2 className="animate-spin" size={16} />{work.gettingLocation ? 'GPS...' : 'Avvio...'}</>
                  ) : (
                    <><Play size={16} />Inizia Lavoro Veicolo</>
                  )}
                </button>
              )}

              {isInProgress && !showingEndForm && (
                <button
                  onClick={() => work.openEndForm(inst.id)}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm"
                  data-testid={`open-end-installation-btn-${inst.id || idx}`}
                >
                  <Square size={16} />Termina Lavoro Veicolo
                </button>
              )}

              {isInProgress && showingEndForm && (
                <div className="space-y-3 bg-white p-3 rounded-lg border border-green-200 mt-2" data-testid={`end-installation-form-${inst.id || idx}`}>
                  <p className="font-medium text-gray-800 text-sm">Esito veicolo</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => work.setEndFormField(inst.id, 'status', 'completed')}
                      className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                        form.status === 'completed' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                      }`}
                      data-testid={`end-installation-completed-${inst.id || idx}`}
                    >
                      <Check className="text-green-500" size={20} />
                      <span className="text-xs font-medium">Completato</span>
                    </button>
                    <button
                      onClick={() => work.setEndFormField(inst.id, 'status', 'not_completed')}
                      className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                        form.status === 'not_completed' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'
                      }`}
                      data-testid={`end-installation-not-completed-${inst.id || idx}`}
                    >
                      <XCircle className="text-red-500" size={20} />
                      <span className="text-xs font-medium">Non completato</span>
                    </button>
                  </div>

                  {form.status === 'not_completed' && (
                    <div className="space-y-2" data-testid={`not-completed-section-${inst.id || idx}`}>
                      <select
                        value={form.category}
                        onChange={(e) => work.setEndFormField(inst.id, 'category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        data-testid={`end-installation-reason-category-${inst.id || idx}`}
                      >
                        <option value="">Seleziona causale...</option>
                        <option value="cliente_assente">Cliente assente</option>
                        <option value="veicolo_non_disponibile">Veicolo non disponibile</option>
                        <option value="materiale_mancante">Materiale/Dispositivo mancante</option>
                        <option value="dato_errato">Dati errati o mancanti</option>
                        <option value="impossibilita_tecnica">Impossibilità tecnica</option>
                        <option value="annullato_cliente">Annullato dal cliente</option>
                        <option value="condizioni_meteo">Condizioni meteo</option>
                        <option value="altro">Altro</option>
                      </select>
                      <textarea
                        value={form.reason}
                        onChange={(e) => work.setEndFormField(inst.id, 'reason', e.target.value)}
                        placeholder="Dettagli aggiuntivi (obbligatorio)..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        rows={2}
                        data-testid={`end-installation-reason-${inst.id || idx}`}
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={work.cancelEndForm}
                      className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={() => work.endInstallation(inst.id)}
                      disabled={busy || work.gettingLocation}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                      data-testid={`confirm-end-installation-btn-${inst.id || idx}`}
                    >
                      {(busy || work.gettingLocation) ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <><Square size={14} />Conferma</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Riepilogo aggregato quando tutte chiuse */}
      {(appointment.status === 'pending_validation' || appointment.status === 'not_completed') && (
        <div className={`mt-3 p-3 rounded-lg text-sm ${
          appointment.status === 'pending_validation'
            ? 'bg-amber-50 border border-amber-200 text-amber-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`} data-testid="per-installation-aggregate-summary">
          <p className="font-semibold">
            {appointment.status === 'pending_validation'
              ? 'Tutti i veicoli chiusi - In attesa validazione Salesforce'
              : 'Tutti i veicoli chiusi come "Non completato"'}
          </p>
          <p className="text-xs mt-1 opacity-80">
            L&apos;appuntamento sarà validato solo quando Salesforce confermerà i job.
          </p>
        </div>
      )}
    </div>
  );
};

export default PerInstallationWorkTrackingPanel;
